import {
    all,
    call,
    fork,
    put,
    select,
    PutEffect,
    takeLatest
} from 'redux-saga/effects';
import axios, { AxiosResponse, AxiosError } from 'axios';
import keyBy from 'lodash.keyby';
import { SagaIterator } from 'redux-saga';
import diff from 'lodash.difference';

// Global Utils
// @ts-ignore
import axiosPoll from 'utils/axiosPoll';
import { takeLatestWithCancel } from 'utils/redux-saga';

// Global Selectors
// @ts-ignore
import { selectCurrentOrganizationId } from 'services/currentOrganizationId/selectors';
// @ts-ignore
import { idTokenSelector } from 'services/auth/selectors';

// Global Services
import {
    setSubmitHasFailed,
    deleteItems
} from 'services/virtualizedTable/actions';
import { updateQueryString } from 'services/router/actions';

import { ERROR_MODAL_QUERY_STRING_PARAM } from 'components/ErrorModal/constants';

// Local Types
import {
    CropsurePassword,
    RequestCreateCropsurePasswordAction,
    RequestDeleteCropsurePasswordAction,
    RequestUpdateCropsurePasswordAction
} from './types';

// Local Constants
import {
    CROPSURE_PASSWORDS_LOAD_REQUESTED,
    CROPSURE_PASSWORDS_LOAD_CANCELED,
    CROPSURE_PASSWORD_CREATE_REQUESTED,
    CROPSURE_PASSWORD_CREATE_NAMESPACE,
    CROPSURE_PASSWORD_UPDATE_REQUESTED,
    CROPSURE_PASSWORD_UPDATE_NAMESPACE,
    CROPSURE_PASSWORD_DELETE_NAMESPACE,
    CROPSURE_PASSWORD_DELETE_REQUESTED
} from './constants';

// Local Actions
import {
    succeededLoadCropsurePasswords,
    failedLoadCropsurePasswords,
    succeededCreateCropsurePassword,
    failedCreateCropsurePassword,
    succeededUpdateCropsurePassword,
    failedUpdateCropsurePassword,
    setCropsurePasswords,
    deleteCropsurePasswords,
    succeededDeleteCropsurePassword,
    failedDeleteCropsurePassword,
    requestLoadCropsurePasswords
} from './actions';

// Local Selectors
import { selectCropsurePasswordUuidsArray } from './selectors';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* loadCropsurePasswordsSucceeded(
    response: AxiosResponse
): SagaIterator {
    // Succeeded Load Spray Events
    const currentUuids = yield select(selectCropsurePasswordUuidsArray);
    const newUuids = response.data.map(({ uuid }: CropsurePassword) => uuid);
    const deletedUuids: string[] = diff(currentUuids, newUuids);
    const tasks: PutEffect<any>[] = [
        put(setCropsurePasswords(keyBy(response.data, 'uuid'))),
        put(succeededLoadCropsurePasswords(response))
    ];

    if (deletedUuids.length) {
        tasks.push(put(deleteCropsurePasswords(deletedUuids)));
    }

    yield all(tasks);
}

export function* loadCropsurePasswordsFailed(error: AxiosError): SagaIterator {
    yield put(failedLoadCropsurePasswords(error));
}

export function* loadCropsurePasswords(): SagaIterator {
    const organizationId = yield select(selectCurrentOrganizationId);

    yield call(axiosPoll, {
        config: {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/CropsurePasswords`,
            params: {
                organizationId,
                filter: {
                    require: { deletedAt: { $exists: false } }
                }
            }
        },
        succeeded: loadCropsurePasswordsSucceeded,
        failed: loadCropsurePasswordsFailed
    });
}

export function* watchCropsurePasswordsLoadRequested(): SagaIterator {
    yield takeLatestWithCancel(
        CROPSURE_PASSWORDS_LOAD_REQUESTED,
        CROPSURE_PASSWORDS_LOAD_CANCELED,
        loadCropsurePasswords
    );
}

export function* createCropsurePassword(
    action: RequestCreateCropsurePasswordAction
): SagaIterator {
    const [idToken, organizationId] = yield all([
        select(idTokenSelector),
        select(selectCurrentOrganizationId)
    ]);
    const url = `${REACT_APP_API_URL}/v1/CropsurePasswords`;

    try {
        const response = yield call(axios, url, {
            method: 'post',
            headers: {
                Authorization: idToken
            },
            data: {
                organizationId,
                ...action.payload
            }
        });
        const { data: cropsurePassword } = response;

        yield all([
            put(succeededCreateCropsurePassword(response)),
            put(
                setCropsurePasswords({
                    [cropsurePassword.uuid]: cropsurePassword
                })
            )
        ]);
    } catch (error) {
        yield all([
            put(failedCreateCropsurePassword(error)),
            put(setSubmitHasFailed(true)),
            put(deleteItems([action.payload.uuid])),
            put(
                updateQueryString({
                    [ERROR_MODAL_QUERY_STRING_PARAM]:
                        CROPSURE_PASSWORD_CREATE_NAMESPACE
                })
            )
        ]);
    }
}

export function* watchCropsurePasswordCreateRequested(): SagaIterator {
    yield takeLatest(
        CROPSURE_PASSWORD_CREATE_REQUESTED,
        createCropsurePassword
    );
}

export function* updateCropsurePassword(
    action: RequestUpdateCropsurePasswordAction
): SagaIterator {
    const idToken = yield select(idTokenSelector);
    const { id, ...payload } = action.payload;

    try {
        const url = `${REACT_APP_API_URL}/v1/CropsurePasswords/${id}`;
        const response = yield call(axios, url, {
            method: 'patch',
            headers: {
                Authorization: idToken
            },
            data: payload
        });
        const { data: cropsurePassword } = response;

        yield all([
            put(succeededUpdateCropsurePassword(response)),
            put(
                setCropsurePasswords({
                    [cropsurePassword.uuid]: cropsurePassword
                })
            )
        ]);
    } catch (error) {
        yield all([
            put(failedUpdateCropsurePassword(error)),
            put(setSubmitHasFailed(true)),
            put(
                updateQueryString({
                    [ERROR_MODAL_QUERY_STRING_PARAM]:
                        CROPSURE_PASSWORD_UPDATE_NAMESPACE
                })
            )
        ]);
    }
}

export function* watchCropsurePasswordUpdateRequested(): SagaIterator {
    yield takeLatest(
        CROPSURE_PASSWORD_UPDATE_REQUESTED,
        updateCropsurePassword
    );
}

export function* deleteCropsurePassword(
    action: RequestDeleteCropsurePasswordAction
): SagaIterator {
    const idToken = yield select(idTokenSelector);
    const { id } = action.payload;
    const url = `${REACT_APP_API_URL}/v1/CropsurePasswords/${id}`;

    try {
        const response = yield call(axios, url, {
            method: 'delete',
            headers: {
                Authorization: idToken
            }
        });

        yield all([
            put(succeededDeleteCropsurePassword(response)),
            put(requestLoadCropsurePasswords())
        ]);
    } catch (error) {
        yield all([
            put(failedDeleteCropsurePassword(error)),
            put(setSubmitHasFailed(true)),
            put(
                updateQueryString({
                    [ERROR_MODAL_QUERY_STRING_PARAM]:
                        CROPSURE_PASSWORD_DELETE_NAMESPACE
                })
            )
        ]);
    }
}

export function* watchCropsurePasswordDeleteRequested(): SagaIterator {
    yield takeLatest(
        CROPSURE_PASSWORD_DELETE_REQUESTED,
        deleteCropsurePassword
    );
}

export default function* cropsurePasswordsSagas(): SagaIterator {
    yield all([
        fork(watchCropsurePasswordsLoadRequested),
        fork(watchCropsurePasswordCreateRequested),
        fork(watchCropsurePasswordUpdateRequested),
        fork(watchCropsurePasswordDeleteRequested)
    ]);
}
